import React, {
  type ClipboardEvent,
  type FocusEvent,
  type ReactElement,
  useCallback,
  useState,
} from 'react';
import { Icon, InlineMessage, InputField } from '@saviynt/design-system';

import { FieldLabel } from '../../../../shared/components';
import {
  SENSITIVE_FIELD_MASK,
  testIds,
  VALIDATION_TYPES,
} from '../../../../shared/constants';
import { type SensitiveField } from '../../../../shared/types';
import { HELPTEXT_VALIDATION } from '../../../FormBuilder/data-layer/validation/validationRules';

import styles from './PreviewFields.module.css';

interface SensitiveProps {
  field: SensitiveField;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  isRequired?: boolean;
  onSentinelCleared?: (fieldName: string) => void;
}

function Sensitive({
  field,
  value,
  onChange,
  error,
  isRequired: isRequiredProp,
  onSentinelCleared,
}: SensitiveProps): ReactElement {
  const [isVisible, setIsVisible] = useState(false);

  const { fieldName, label, helperText, infoTooltipText, validations } = field;

  const isSentinel = value === SENSITIVE_FIELD_MASK;

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if (isSentinel && e.target instanceof HTMLInputElement) {
        onChange?.('');
        onSentinelCleared?.(fieldName);
      }
    },
    [isSentinel, onChange, onSentinelCleared, fieldName]
  );

  // Use prop if provided, otherwise fallback to validation rules
  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;

  // TODO: Modern browsers have broken autocomplete behavior with type='password'
  // Note: Masked editing only supports append/delete at end. Use visibility toggle for mid-string edits.
  // Mask value with bullet points when not visible
  const displayValue = isVisible
    ? (value ?? '')
    : '•'.repeat((value ?? '').length);

  // Intercept copy so the real value is placed on the clipboard, not bullet chars.
  // When sentinel, suppress copy entirely (sentinel is not the real value).
  const handleCopy = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      if (isSentinel) {
        e.preventDefault();

        return;
      }

      if (!isVisible && value) {
        e.clipboardData.setData('text/plain', value);
        e.preventDefault();
      }
    },
    [isVisible, isSentinel, value]
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      onCopy={handleCopy}
      onFocus={handleFocus}
      className={styles.sensitiveFieldWrapper}>
      <InputField
        id={fieldName}
        name={fieldName}
        size='medium'
        placeholder=''
        value={displayValue}
        setValue={(v: string) => {
          if (isVisible) {
            // When visible, update normally
            onChange?.(v);
          } else {
            // When masked, calculate the actual value based on input changes
            const currentLength = (value ?? '').length;
            const newLength = v.length;

            if (newLength > currentLength) {
              // Characters added - append the new characters (skip bullet points)
              const addedChars = v.slice(currentLength).replace(/•/g, '');

              onChange?.((value ?? '') + addedChars);
            } else if (newLength < currentLength) {
              // Characters removed - remove from end
              onChange?.((value ?? '').slice(0, newLength));
            }
          }
        }}
        label={
          <FieldLabel
            label={label}
            htmlFor={fieldName}
            isReadOnly={false}
            infoTooltipText={infoTooltipText}
          />
        }
        suffixButtonType='password'
        suffixIsToggle={isVisible}
        suffixOnClick={() => setIsVisible(!isVisible)}
        isRequired={isRequired}
        tabIndex={0}
        isCritical={!!error}
        CriticalHelperText={
          <InlineMessage
            text={error}
            colorTheme='critical'
            size='small'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        }
        HelperText={
          helperText ? (
            <InlineMessage
              text={helperText.slice(0, HELPTEXT_VALIDATION.MAX_LENGTH)}
              colorTheme='neutral'
              size='small'
            />
          ) : undefined
        }
        dataTestId={testIds.FF_PREVIEW_SENSITIVE}
      />
    </div>
  );
}

export default Sensitive;
